import React from "react";
import { Grid, Card } from "@mui/material";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiSelect from "src/components/core-components/mui/ByzMuiSelect";
import { useTranslate } from "src/locales";

interface PartographParametersProps {
    traceForm: any;
    handleTraceChange: (key: string, value: any) => void;
    rowSelectionModel: GridRowSelectionModel;
    isAdding: boolean;
    painLevelOptions: any[];
    amnionFluidOptions: any[];
}

const PartographParameters: React.FC<PartographParametersProps> = ({
    traceForm,
    handleTraceChange,
    rowSelectionModel,
    isAdding,
    painLevelOptions,
    amnionFluidOptions,
}) => {
    const { t } = useTranslate();
    const isDisabled = rowSelectionModel.length === 0 && !isAdding;

    return (
        <Card sx={{ marginBottom: "5px" }}>
            <Grid
                container
                sx={{
                    m: 1,
                    width: "calc(100% - 16px)",
                    p: 2,
                    borderBottom: "1px solid #E0E0E0",
                    backgroundColor: "#63B4FF33",
                    borderRadius: "10px",
                }}
            >
                <span style={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                    {t("partograf-form.parameters") || "Parametreler"}
                </span>
            </Grid>

            <Grid
                item
                xs={12}
                md={12}
                xl={12}
                container
                spacing={3}
                sx={{
                    display: {
                        md: "flex",
                        xl: "flex",
                        xs: "flex",
                    },
                }}
            >
                <Grid item xs="auto" container sx={{ marginLeft: "10px" }}>
                    <Grid item xs={12} md={12} xl={12}>
                        <Grid
                            item
                            xs={12}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: {
                                    md: "flex-start",
                                    xl: "flex-start",
                                    sm: "flex-start",
                                    xs: "center",
                                },
                                padding: "8px 0",
                                justifyContent: {
                                    md: "left",
                                    xl: "left",
                                    xs: "center",
                                },
                            }}
                        >
                            <ByzMuiTypography variant="body1" sx={{ marginBottom: "4px" }}>
                                {t("partograf-form.time-of-measurement")}
                            </ByzMuiTypography>

                            <ByzMuiDateTimePicker
                                disabled={isDisabled}
                                sx={{
                                    width: "240px !important",
                                }}
                                label=""
                                format="dd/MM/yyyy HH:mm"
                                value={traceForm.PROCESS_TIME || null}
                                onChange={(val: any) => handleTraceChange("PROCESS_TIME", val)}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs="auto" container sx={{ marginLeft: "10px" }}>
                    <Grid item xs={12} md={12} xl={12}>
                        <Grid
                            item
                            xs={12}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: {
                                    md: "flex-start",
                                    xl: "flex-start",
                                    sm: "flex-start",
                                    xs: "center",
                                },
                                padding: "8px 0",
                                justifyContent: {
                                    md: "left",
                                    xl: "left",
                                    xs: "center",
                                },
                            }}
                        >
                            <ByzMuiTypography variant="body1" sx={{ marginBottom: "4px" }}>
                                {t("partograf-form.fetal-heart-rate")}
                            </ByzMuiTypography>

                            <ByzMuiTextField
                                disabled={isDisabled}
                                sx={{
                                    width: "240px !important",
                                }}
                                type="number"
                                variant="outlined"
                                label=""
                                value={traceForm.FETAL_HEART_RATE || ""}
                                onChange={(e: any) => {
                                    const val = e.target.value;
                                    if (val === "" || (/^\d+$/.test(val) && Number(val) <= 999)) {
                                        handleTraceChange("FETAL_HEART_RATE", val);
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs="auto" container sx={{ marginLeft: "10px" }}>
                    <Grid item xs={12} md={12} xl={12}>
                        <Grid
                            item
                            xs={12}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: {
                                    md: "flex-start",
                                    xl: "flex-start",
                                    sm: "flex-start",
                                    xs: "center",
                                },
                                padding: "8px 0",
                                justifyContent: {
                                    md: "left",
                                    xl: "left",
                                    xs: "center",
                                },
                            }}
                        >
                            <ByzMuiTypography variant="body1" sx={{ marginBottom: "4px" }}>
                                {t("partograf-form.cervix-dilation")}
                            </ByzMuiTypography>

                            <ByzMuiSelect
                                disabled={isDisabled}
                                sx={{
                                    width: "240px !important",
                                }}
                                renderInput={(params: any) => (
                                    <ByzMuiTextField {...params} label="" variant="outlined" />
                                )}
                                options={Array.from({ length: 10 }, (_, i) => ({ value: i + 1, label: (i + 1).toString() }))}
                                color="primary"
                                label={t("partograf-form.chose")}
                                placeholder={t("partograf-form.chose")}
                                value={
                                    Array.from({ length: 10 }, (_, i) => ({ value: i + 1, label: (i + 1).toString() })).find(
                                        (opt) => String(opt.value) === String(traceForm.SERVIKS_SIZE)
                                    ) || null
                                }
                                onChange={(_e: any, option: any) =>
                                    handleTraceChange("SERVIKS_SIZE", option?.value ?? "")
                                }
                            />
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs="auto" container sx={{ marginLeft: "10px" }}>
                    <Grid item xs={12} md={12} xl={12}>
                        <Grid
                            item
                            xs={12}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: {
                                    md: "flex-start",
                                    xl: "flex-start",
                                    sm: "flex-start",
                                    xs: "center",
                                },
                                padding: "8px 0",
                                justifyContent: {
                                    md: "left",
                                    xl: "left",
                                    xs: "center",
                                },
                            }}
                        >
                            <ByzMuiTypography variant="body1" sx={{ marginBottom: "4px" }}>
                                {t("partograf-form.head-level")}
                            </ByzMuiTypography>

                            <ByzMuiSelect
                                disabled={isDisabled}
                                sx={{
                                    width: "240px !important",
                                }}
                                renderInput={(params: any) => (
                                    <ByzMuiTextField {...params} label="" variant="outlined" />
                                )}
                                options={[-3, -2, -1, 0, 1, 2, 3].map(i => ({ value: i, label: i.toString() }))}
                                color="primary"
                                label={t("partograf-form.chose")}
                                placeholder={t("partograf-form.chose")}
                                value={
                                    [-3, -2, -1, 0, 1, 2, 3].map(i => ({ value: i, label: i.toString() })).find(
                                        (opt) => String(opt.value) === String(traceForm.HEAD_LEVEL)
                                    ) || null
                                }
                                onChange={(_e: any, option: any) =>
                                    handleTraceChange("HEAD_LEVEL", option?.value ?? "")
                                }
                            />
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs="auto" container sx={{ marginLeft: "10px" }}>
                    <Grid item xs={12} md={12} xl={12}>
                        <Grid
                            item
                            xs={12}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: {
                                    md: "flex-start",
                                    xl: "flex-start",
                                    sm: "flex-start",
                                    xs: "center",
                                },
                                padding: "8px 0",
                                justifyContent: {
                                    md: "left",
                                    xl: "left",
                                    xs: "center",
                                },
                            }}
                        >
                            <ByzMuiTypography variant="body1" sx={{ marginBottom: "4px", minWidth: "240px" }}>
                                {t("partograf-form.contraction-pain-level")}
                            </ByzMuiTypography>

                            <ByzMuiSelect
                                disabled={isDisabled}
                                sx={{
                                    width: "240px !important",
                                }}
                                renderInput={(params: any) => (
                                    <ByzMuiTextField {...params} label="" variant="outlined" />
                                )}
                                options={painLevelOptions}
                                color="primary"
                                label={t("partograf-form.chose")}
                                placeholder={t("partograf-form.chose")}
                                value={
                                    painLevelOptions.find(
                                        (opt) => String(opt.value) === String(traceForm.PAIN_LEVEL)
                                    ) || null
                                }
                                onChange={(_e: any, option: any) =>
                                    handleTraceChange("PAIN_LEVEL", option?.value ?? "")
                                }
                            />
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs="auto" container sx={{ marginLeft: "10px" }}>
                    <Grid item xs={12} md={12} xl={12}>
                        <Grid
                            item
                            xs={12}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: {
                                    md: "flex-start",
                                    xl: "flex-start",
                                    sm: "flex-start",
                                    xs: "center",
                                },
                                padding: "8px 0",
                                justifyContent: {
                                    md: "left",
                                    xl: "left",
                                    xs: "center",
                                },
                            }}
                        >
                            <ByzMuiTypography variant="body1" sx={{ marginBottom: "4px" }}>
                                {t("partograf-form.pain-count")}
                            </ByzMuiTypography>

                            <ByzMuiSelect
                                disabled={isDisabled}
                                sx={{
                                    width: "240px !important",
                                }}
                                renderInput={(params: any) => (
                                    <ByzMuiTextField {...params} label="" variant="outlined" />
                                )}
                                options={[1, 2, 3, 4, 5].map(i => ({ value: i, label: i.toString() }))}
                                color="primary"
                                label={t("partograf-form.chose")}
                                placeholder={t("partograf-form.chose")}
                                value={
                                    [1, 2, 3, 4, 5].map(i => ({ value: i, label: i.toString() })).find(
                                        (opt) => String(opt.value) === String(traceForm.PAIN_COUNT)
                                    ) || null
                                }
                                onChange={(_e: any, option: any) =>
                                    handleTraceChange("PAIN_COUNT", option?.value ?? "")
                                }
                            />
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs="auto" container sx={{ marginLeft: "10px" }}>
                    <Grid item xs={12} md={12} xl={12}>
                        <Grid
                            item
                            xs={12}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: {
                                    md: "flex-start",
                                    xl: "flex-start",
                                    sm: "flex-start",
                                    xs: "center",
                                },
                                padding: "8px 0",
                                justifyContent: {
                                    md: "left",
                                    xl: "left",
                                    xs: "center",
                                },
                            }}
                        >
                            <ByzMuiTypography variant="body1" sx={{ marginBottom: "4px", minWidth: "240px" }}>
                                {t("partograf-form.amniotic-fluid")}
                            </ByzMuiTypography>

                            <ByzMuiSelect
                                disabled={isDisabled}
                                sx={{
                                    width: "240px !important",
                                }}
                                renderInput={(params: any) => (
                                    <ByzMuiTextField {...params} label="" variant="outlined" />
                                )}
                                options={amnionFluidOptions}
                                color="primary"
                                label={t("partograf-form.chose")}
                                placeholder={t("partograf-form.chose")}
                                value={
                                    amnionFluidOptions.find(
                                        (opt) => String(opt.value) === String(traceForm.AMNION_FLUID)
                                    ) || null
                                }
                                onChange={(_e: any, option: any) =>
                                    handleTraceChange("AMNION_FLUID", option?.value ?? "")
                                }
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Card>
    );
};

export default PartographParameters;
